import { Injectable } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { NotificationStateService } from "./notification-state.service";
import { environment } from "src/environments/environment";

// Importações do Capacitor
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { ConfigStateService, RestService } from '@abp/ng.core';

/**
 * Canal Android das notificações do CargoFleet. O backend manda o push apontando para
 * este id (NotificationConsts.CanalPushMobile) e o AndroidManifest usa o mesmo como
 * default_notification_channel_id — mudou aqui, mudou lá.
 */
export const CANAL_NOTIFICACOES = 'cargofleet-alertas';

/**
 * Canal antigo. Nasceu com `sound: 'default'`, que o plugin traduz para
 * android.resource://<pacote>/raw/default — arquivo que não existe — e por isso
 * a notificação saía muda. Canal criado é imutável, então trocamos o id e
 * apagamos o velho para ele sumir das configurações do aparelho.
 */
const CANAL_LEGADO = 'fcm-foreground';

@Injectable({
  providedIn: 'root'
})
export class FcmService {
  private messaging: any;
  private webListenerActive = false;

  private log(message: string) {
    if (!environment.production) console.log('[FCM] ' + message);
  }

  private logWarn(message: string) {
    if (!environment.production) console.warn('[FCM] ' + message);
  }

  private logError(message: string) {
    if (!environment.production) console.error('[FCM] ' + message);
  }

  constructor(
    private notificationStateService: NotificationStateService,
    private restService: RestService,
    private config: ConfigStateService,
  ) {}

  async init(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await this.initMobilePush();
    } else {
      await this.initWebPush();
    }
  }

  // Lógica de inicialização para WEB
  private async initWebPush(): Promise<void> {
    if (this.messaging) return; // já inicializado
    const isSupportedBrowser = await isSupported();
    if (!isSupportedBrowser) {
      this.logWarn('Web Push não suportado neste navegador.');
      return;
    }
    const firebaseApp = initializeApp(environment.firebaseConfig);
    this.messaging = getMessaging(firebaseApp);
    this.listenToWebMessages();
  }

  // Lógica de inicialização para MOBILE
  private async initMobilePush(): Promise<void> {
    // Todo o corpo está dentro de um try/catch: a init do push é um ponto
    // sensível do arranque, e nada aqui pode rejeitar e travar o boot.
    try {
      await LocalNotifications.requestPermissions();

      if (Capacitor.getPlatform() === 'android') {
        await this.garantirCanalAndroid();
      }

      PushNotifications.addListener('registration', (token: Token) => {
        this.log('Token registrado: ' + token.value);

        const currentUser = this.config.getOne("currentUser");

        // Só envia com um usuário autenticado: sem userId o backend rejeita o
        // device-token com 400. Quando o usuário loga, o app.component chama o
        // register() de novo e o token é reenviado, aí já com o userId.
        if (token?.value && currentUser?.id) {
          this.restService.request<any, any>(
            {
              method: 'POST',
              url: '/api/app/device-token',
              body: {
                tenantId: currentUser.tenantId,
                userId: currentUser.id,
                token: token.value
              }
            },
            { apiName: "Default" }
          ).subscribe({
            error: (err) => {
              this.logError('Erro ao salvar o token: ' + JSON.stringify(err));
            }
          });
        }
      });

      PushNotifications.addListener('registrationError', (error: any) => {
        this.logError('Erro no registro: ' + JSON.stringify(error));
      });

      PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
        this.notificationStateService.incrementUnreadCount();

        // Este evento só dispara com o app em primeiro plano; em segundo plano o
        // SDK nativo exibe o push sozinho, no canal que o backend indica. Em
        // primeiro plano o Android não mostra nada, então reexibimos como
        // notificação local.
        try {
          await LocalNotifications.schedule({
            notifications: [
              {
                // O plugin rejeita id fora de int32 (LocalNotification.java: "The identifier
                // should be a Java int") e Date.now() já passa de 1,7e12 — com ele o schedule
                // inteiro era recusado e nada aparecia em primeiro plano.
                id: Date.now() % 0x7fffffff,
                title: notification.title ?? '',
                body: notification.body ?? '',
                channelId: CANAL_NOTIFICACOES,
              }
            ]
          });
        } catch (err) {
          this.logError('Erro ao exibir notificação local: ' + JSON.stringify(err));
        }
      });

      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        this.log('Notificação tocada: ' + JSON.stringify(action));
      });
    } catch (err) {
      this.logError('Erro ao inicializar o push mobile: ' + JSON.stringify(err));
    }
  }

  /**
   * Cria o canal Android usado tanto pela notificação local (primeiro plano)
   * quanto pelo push exibido pelo SDK nativo (segundo plano).
   *
   * `sound` é o nome de um arquivo em android/app/src/main/res/raw/ — é esse
   * que vai no APK; nada copia de angular/resources/ para lá. O plugin tira a extensão e monta
   * android.resource://<pacote>/raw/<nome> — por isso `sound: 'default'` NÃO
   * é o toque do sistema: vira um recurso inexistente e a notificação sai muda.
   * Canal criado é imutável: para trocar o som depois, troque também o id
   * (CANAL_NOTIFICACOES aqui, NotificationConsts.CanalPushMobile no backend e o
   * meta-data do AndroidManifest).
   */
  private async garantirCanalAndroid(): Promise<void> {
    try {
      await LocalNotifications.createChannel({
        id: CANAL_NOTIFICACOES,
        name: 'Notificações',
        importance: 4, // NotificationManager.IMPORTANCE_HIGH — banner (heads-up) + som (5 é MAX, "unused" na doc do Android)
        visibility: 1, // VISIBILITY_PUBLIC
        sound: 'cargo_fleet_sound_v1.ogg',
        vibration: true,
      });
    } catch (err) {
      this.logError('Erro ao criar canal de notificação: ' + JSON.stringify(err));
    }

    try {
      await LocalNotifications.deleteChannel({ id: CANAL_LEGADO });
    } catch (err) {
      this.logWarn('Erro ao remover canal legado: ' + JSON.stringify(err));
    }
  }


  async requestPermissionAndGetToken(): Promise<string | null> {
    if (Capacitor.isNativePlatform()) {
      const result = await PushNotifications.requestPermissions();
      if (result.receive === 'granted') {
        await PushNotifications.register();
        return null;
      } else {
        this.logWarn('Permissão para notificações não concedida.');
        return null;
      }
    } else {
      if (!this.messaging) {
        await this.initWebPush();
      }
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          const token = await getToken(this.messaging, {
            vapidKey: environment.vapidKey
          });
          this.listenToWebMessages();
          return token;
        } else {
          this.logWarn('Permissão para notificações web não concedida.');
          return null;
        }
      } catch (error) {
        this.logError('Erro ao obter token web: ' + JSON.stringify(error));
        return null;
      }
    }
  }

  // Listener para mensagens WEB quando o app está em primeiro plano
  private listenToWebMessages() {
    if (!this.messaging || this.webListenerActive) return;
    this.webListenerActive = true;
    onMessage(this.messaging, (payload) => {
      this.notificationStateService.incrementUnreadCount();
    });
  }
}
